import React, { FC, useCallback, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { t } from 'i18next';
import { useQueryClient } from '@tanstack/react-query';
import MessageRounded from '@mui/icons-material/MessageRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';

import {
  ColumnProps,
  DeleteDialog,
  Page,
  PageHeaderAction,
  showNotification,
  Table,
  useDialogState,
  useFilterConfig,
  usePanelState,
  useTableQueryParams,
} from 'components';
import { parse, stringify } from 'utils/base64';
import { getValueSetValues } from 'utils/valueSetFunctions';
import { getFieldDataAndAccessor } from 'utils/useFieldDataAndAccessor';
import {
  useDeleteWorker,
  useExportWorkers,
  useLatestSyncJob,
  useSyncTask,
  useSyncWorkers,
  useWorkerListSearch,
  useWorkerPageList,
  workersKeys,
} from 'features/workers';
import { FilterOptionModel, NotificationType, QueryParamModel, WorkerViewModel } from 'models';
import LeoSelectFieldMulti from 'components/input-fields/leoSelectField/LeoSelectFieldMulti';
import LeoTextField from 'components/input-fields/leoTextField/LeoTextField';
import LeoSelectField from 'components/input-fields/leoSelectField/LeoSelectField';
import LeoCheckBox from 'components/input-fields/leoCheckBox/LeoCheckBox';
import { useValueSetList } from 'features/valueSets';
import { usePermissionGroupListSearch } from 'features/permissionGroups';
import { formatAddress, getPageName } from 'utils/format';
import { formatDate } from 'utils/dates';
import { RouteNames } from 'config/routeNames';
import WorkerEditorDialog from 'pages/Kozos/Workers/WorkersEditorDialog';
import { useCanEdit } from 'utils/auth';

import { ReactComponent as Export } from 'assets/icons/Material/fileDownload.svg';

import SendMessageDialog from './SendMessageDialog';
import SyncStatusPopover from './SyncStatusPopover';
import PhotoQcCheckbox from './PhotoQcCheckbox';

const WorkersPage: FC = () => {
  const {
    onPageChange,
    onRowsPerPageChange,
    onSort,
    onFilter,
    onLoadQuery,
    params,
    apiParams,
    exportParams,
    onTableColumnVisibilityChange,
  } = useTableQueryParams({
    order: stringify([{ orderBy: 'sapId' }]),
  });

  const apiParamsWithDeleted = (paramsToConvert: Partial<QueryParamModel>) => {
    const parsedFilter = parse(paramsToConvert?.filter);
    if (parsedFilter?.withDeleted && parsedFilter.withDeleted[0] === 'on') {
      return { ...paramsToConvert, filter: stringify({ ...parsedFilter, withDeleted: true }) };
    }
    return paramsToConvert;
  };

  const {
    isLoading: isExportLoading,
    isRefetching,
    refetch,
  } = useExportWorkers(apiParamsWithDeleted(exportParams));
  const {
    data: workersData,
    isLoading,
    isFetching,
    dataUpdatedAt,
  } = useWorkerPageList(apiParamsWithDeleted(apiParams));
  const { data: filterWorkersData } = useWorkerListSearch({ withDeleted: true });
  const { mutateAsync: syncWorkers, isPending: isSyncLoading } = useSyncWorkers();
  const { mutateAsync: syncTask } = useSyncTask();
  const { data: latestSyncJob, refetch: refetchLatestSyncJob } = useLatestSyncJob();
  const [isSyncTaskLoading, setIsSyncTaskLoading] = useState(false);
  const { data: valueSet, isLoading: isValueSetLoading } = useValueSetList({
    code: ['LEOKOZPONT', 'NEXON_ROLE'],
  });
  const { data: permissionGroups } = usePermissionGroupListSearch();
  const queryClient = useQueryClient();

  const { data: filterWorkerData, accessor: filterWorkerAccessor } = getFieldDataAndAccessor(
    filterWorkersData,
    'sapId',
    'name',
    true
  );

  const { data: readingCenters, accessor: leoKozpontAccessor } = getFieldDataAndAccessor(
    getValueSetValues(valueSet, 'LEOKOZPONT')
  );

  const activePermissionGroups = useMemo(
    () => (permissionGroups ?? []).filter((group) => group.isActive),
    [permissionGroups]
  );

  const { data: permissionGroupsData, accessor: permissionGroupsAccessor } =
    getFieldDataAndAccessor(activePermissionGroups, 'id', 'name');

  const { data: nexonRoles, accessor: nexonRoleAccessor } = getFieldDataAndAccessor(
    getValueSetValues(valueSet, 'NEXON_ROLE')
  );

  const visible = useCanEdit('WORKERS');

  const {
    isOpen: isEditorOpen,
    openDialog: openEditor,
    closeDialog: closeEditor,
    selected: selectedEdit,
  } = useDialogState<WorkerViewModel>();

  const {
    isOpen: isMessageOpen,
    openDialog: openMessage,
    closeDialog: closeMessage,
    selected: selectedSendMessage,
  } = useDialogState<WorkerViewModel[]>();

  const {
    isOpen: isDeleteOpen,
    openDialog: openDelete,
    closeDialog: closeDelete,
    selected: selectedDelete,
  } = useDialogState<WorkerViewModel>();

  const { mutateAsync: deleteWorker, isPending: isDeleting } = useDeleteWorker();

  const { activePanel, closePanel, setPanel } = usePanelState();

  const filters: FilterOptionModel[] = [
    {
      headerTitle: 'MUNKAVEGZOK.SAP_ID',
      component: (
        <LeoSelectFieldMulti
          name="sapId"
          data={filterWorkerData}
          accessor={filterWorkerAccessor}
          label="MUNKAVEGZOK.SAPID"
          freeSolo
        />
      ),
      name: 'sapId',
      divider: true,
      panelFieldWidth: 12,
    },
    {
      headerTitle: 'MUNKAVEGZOK.AD_USER',
      component: <LeoTextField name="email" label={<Trans i18nKey="MUNKAVEGZOK.AD_USER" />} />,
      name: 'email',
      panelFieldWidth: 12,
      divider: true,
      abbrName: t('FILTERABBR.AD_USER'),
    },
    {
      headerTitle: 'MUNKAVEGZOK.READING_CENTER',
      component: (
        <LeoSelectField
          name="readingCenter"
          data={readingCenters}
          label="MUNKAVEGZOK.READING_CENTER"
        />
      ),
      name: 'readingCenter',
      panelFieldWidth: 12,
      divider: true,
      abbrName: t('FILTERABBR.READING_CENTER'),
    },
    {
      headerTitle: 'MUNKAVEGZOK.OPEM_ROLE',
      component: (
        <LeoSelectFieldMulti
          name="permissionGroups"
          data={permissionGroupsData}
          accessor={permissionGroupsAccessor}
          label="MUNKAVEGZOK.OPEM_ROLE"
        />
      ),
      name: 'permissionGroups',
      panelFieldWidth: 12,
      abbrName: t('FILTERABBR.OPEM_ROLE'),
    },
    {
      headerTitle: 'MUNKAVEGZOK.NEXON_ROLE',
      component: (
        <LeoSelectField
          name="nexonRole"
          data={nexonRoles}
          accessor={nexonRoleAccessor}
          label="MUNKAVEGZOK.NEXON_ROLE"
        />
      ),
      name: 'nexonRole',
      panelFieldWidth: 12,
      abbrName: t('FILTERABBR.NEXON_ROLE'),
    },
    {
      headerTitle: 'MUNKAVEGZOK.INACTIVE_USERS',
      component: (
        <LeoCheckBox
          name="withDeleted"
          label={<Trans i18nKey="MUNKAVEGZOK.SHOW_INACTIVE_USERS" />}
        />
      ),
      name: 'withDeleted',
      panelFieldWidth: 12,
      abbrName: t('FILTERABBR.INACTIVES'),
    },
  ];

  const filterConfig = useFilterConfig({
    params,
    onFilter,
    filters,
    onLoad: onLoadQuery,
    isButton: true,
    pageName: getPageName(RouteNames.KOZOS_MUNKAVEGZOK),
    isFetching,
  });

  const panels = [filterConfig];

  const onDeleteWorker = async () => {
    if (selectedDelete?.id) {
      await deleteWorker(selectedDelete.id);
      closeDelete();
    }
  };

  const workersColumns: ColumnProps<WorkerViewModel>[] = [
    {
      key: 'readingCenter',
      header: <Trans i18nKey="MUNKAVEGZOK.READING_CENTER" />,
      accessor: 'readingCenter',
      tooltip: (item: WorkerViewModel) =>
        item.readingCenter && leoKozpontAccessor[item.readingCenter],
      sortable: true,
    },
    {
      key: 'sapId',
      header: <Trans i18nKey="MUNKAVEGZOK.SAP_ID" />,
      accessor: 'sapId',
      sortable: true,
    },
    {
      key: 'email',
      header: <Trans i18nKey="MUNKAVEGZOK.AD_USER" />,
      accessor: 'email',
      sortable: true,
    },
    {
      key: 'name',
      header: <Trans i18nKey="MUNKAVEGZOK.NAME" />,
      accessor: 'name',
      sortable: true,
    },
    {
      key: 'internalId',
      header: <Trans i18nKey="MUNKAVEGZOK.INTERNAL_ID" />,
      accessor: 'internalId',
      sortable: true,
    },
    {
      key: 'address',
      header: <Trans i18nKey="MUNKAVEGZOK.ADDRESS" />,
      accessor: (worker: WorkerViewModel) => formatAddress(worker.address),
      field: 'address.city',
      sortable: true,
    },
    {
      key: 'phone',
      header: <Trans i18nKey="MUNKAVEGZOK.PHONE" />,
      accessor: 'phone',
      sortable: true,
    },
    {
      key: 'employmentStartDate',
      header: <Trans i18nKey="MUNKAVEGZOK.EMPLOYMENT_START_DATE" />,
      accessor: (worker: WorkerViewModel) => formatDate(worker.employmentStartDate),
      field: 'employmentStartDate',
      sortable: true,
    },
    {
      key: 'employmentEndDate',
      header: <Trans i18nKey="MUNKAVEGZOK.EMPLOYMENT_END_DATE" />,
      accessor: (worker: WorkerViewModel) => formatDate(worker.employmentEndDate),
      field: 'employmentEndDate',
      sortable: true,
    },
    {
      key: 'permissionGroups',
      header: <Trans i18nKey="MUNKAVEGZOK.OPEM_ROLE" />,
      accessor: (worker: WorkerViewModel) =>
        worker.permissionGroups?.map((id) => permissionGroupsAccessor[id]).join(', ') ?? '',
      field: 'permissionGroups',
      sortable: false,
    },
    {
      key: 'nexonRole',
      header: <Trans i18nKey="MUNKAVEGZOK.NEXON_ROLE" />,
      accessor: (worker: WorkerViewModel) =>
        worker.nexonRole ? nexonRoleAccessor[worker.nexonRole] : '',
      field: 'nexonRole',
      sortable: true,
    },
    {
      key: 'isPhotoQcRequired',
      header: <Trans i18nKey="MUNKAVEGZOK.PHOTO_QC" />,
      accessor: (worker: WorkerViewModel) => (
        <PhotoQcCheckbox
          workerId={worker.id}
          checked={!!worker.isPhotoQcRequired}
          disabled={!visible}
        />
      ),
      field: 'isPhotoQcRequired',
      align: 'center',
      sortable: false,
    },
  ];

  const syncById = useCallback(
    async (jobId: string) => {
      setIsSyncTaskLoading(true);
      const { status, failedReason } = await syncTask(jobId);
      if (failedReason) {
        showNotification({
          content: t('MUNKAVEGZOK.SYNC_FAIL'),
          type: NotificationType.ERROR,
        });
        setIsSyncTaskLoading(false);
        refetchLatestSyncJob();
        return;
      }

      if (status !== 'completed') {
        refetchLatestSyncJob();
        setTimeout(async () => syncById(jobId), 3000);
      } else {
        await queryClient.invalidateQueries({ queryKey: workersKeys.lists() });
        showNotification({
          content: t('MUNKAVEGZOK.SYNC_SUCCESS'),
          type: NotificationType.SUCCESS,
        });
        setIsSyncTaskLoading(false);
        refetchLatestSyncJob();
      }
    },
    [queryClient, syncTask, refetchLatestSyncJob]
  );

  const workerHeaderActions = useMemo<PageHeaderAction[]>(
    () => [
      {
        name: 'sync',
        label: 'MUNKAVEGZOK.SYNC_BUTTON',
        icon: <SyncIcon fill="currentColor" />,
        onClick: async () => {
          const { jobId } = await syncWorkers();
          refetchLatestSyncJob();
          await syncById(jobId);
        },
        isLoading: isSyncLoading || isSyncTaskLoading,
        visible,
      },
      {
        customRender: latestSyncJob ? <SyncStatusPopover syncJob={latestSyncJob} /> : null,
        name: 'lastSyncStatus',
        label: '',
      },
      {
        name: 'message',
        label: 'LEO_LEO_EGYSEGEK.BULK_SEND_MESSAGE',
        icon: <MessageRounded fontSize="small" />,
        onClick: (row) => openMessage(row),
        isBulkAction: true,
        visible,
      },
      {
        name: 'export',
        label: 'COMMON.EXPORT',
        icon: <Export fill="currentColor" />,
        onClick: () => refetch(),
        isLoading: isExportLoading || isRefetching,
        disabledIf: !exportParams?.filter,
      },
    ],
    [
      isSyncLoading,
      isSyncTaskLoading,
      visible,
      latestSyncJob,
      isExportLoading,
      isRefetching,
      exportParams?.filter,
      syncWorkers,
      syncById,
      openMessage,
      refetch,
      refetchLatestSyncJob,
    ]
  );

  return (
    <Page
      title={<Trans i18nKey="MUNKAVEGZOK.TITLE" />}
      inScroll
      activePanel={activePanel}
      closePanel={closePanel}
      setPanel={setPanel}
      panels={panels}
    >
      {!isValueSetLoading && (
        <Table
          filterDetailsData={{ leoKozpontAccessor, permissionGroupsAccessor, nexonRoleAccessor }}
          panels={panels}
          setPanel={setPanel}
          activePanel={activePanel}
          timestamp={dataUpdatedAt}
          list={workersData?.data || []}
          total={workersData?.meta?.total}
          loading={isLoading}
          params={params}
          onSort={onSort}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          columns={workersColumns}
          enableCheckbox={visible}
          actions={
            visible
              ? [
                  {
                    name: 'edit',
                    label: 'COMMON.EDIT',
                    icon: <EditIcon fontSize="small" />,
                    onClick: (worker) => openEditor(worker),
                  },
                  {
                    name: 'message',
                    label: 'MUNKAVEGZOK.SEND_MESSAGE',
                    icon: <MessageRounded fontSize="small" />,
                    onClick: (worker) => openMessage(worker),
                  },
                  {
                    name: 'delete',
                    label: 'COMMON.DELETE',
                    icon: <DeleteIcon fontSize="small" />,
                    onClick: (worker) => openDelete(worker),
                  },
                ]
              : []
          }
          pageHeaderActions={workerHeaderActions}
          onTableColumnVisibilityChange={onTableColumnVisibilityChange}
          onLoadQuery={onLoadQuery}
        />
      )}
      <WorkerEditorDialog open={isEditorOpen} selected={selectedEdit} onClose={closeEditor} />
      <SendMessageDialog
        open={isMessageOpen}
        selected={selectedSendMessage}
        onClose={closeMessage}
        isWorkerModel
      />
      <DeleteDialog
        open={isDeleteOpen}
        selected={selectedDelete}
        onClose={closeDelete}
        onSubmit={onDeleteWorker}
        loading={isDeleting}
        deleteTitleAccessor="name"
      />
    </Page>
  );
};

export default WorkersPage;
